import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Sparkles, Droplets, Heart, ChefHat, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";

// Sunday (or Monday) → recap of the week that just ended; other days collapsed behind a link
export function recapWeekEnd() {
  const d = new Date();
  const dow = d.getDay(); // 0 = Sunday
  if (dow === 0) return d.toISOString().slice(0, 10);
  d.setDate(d.getDate() - dow); // last Sunday
  return d.toISOString().slice(0, 10);
}
const isRecapDay = () => [0, 1].includes(new Date().getDay());

function Kpi({ icon: Icon, value, label, testid }) {
  return (
    <div className="rounded-2xl bg-secondary/60 p-3" data-testid={testid}>
      <Icon className="h-4 w-4 text-primary" />
      <p className="mt-1.5 font-serif text-2xl font-semibold leading-none">{value}</p>
      <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{label}</p>
    </div>
  );
}

export function WeeklyRecapCard({ members = [], memberId }) {
  const { t, tv } = useLang();
  const [open, setOpen] = useState(isRecapDay());
  const weekEnd = recapWeekEnd();
  const { data } = useQuery({
    queryKey: ["recap", weekEnd, memberId],
    queryFn: async () => (await api.get("/recap/weekly", { params: { week_end: weekEnd, member_id: memberId } })).data,
    enabled: open,
  });
  const member = members.find((m) => m.member_id === memberId);
  const pct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${Math.round(v * 100)} %`);

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="w-full chenia-card p-4 flex items-center justify-between text-left hover:bg-secondary/40 transition-colors" data-testid="recap-toggle">
        <span className="inline-flex items-center gap-2 font-medium"><Sparkles className="h-4 w-4 text-primary" /> {t("show_recap")} <span className="text-xs text-muted-foreground">· {t("last_week")}</span></span>
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </button>
    );
  }
  return (
    <div className="chenia-card p-6 chenia-fade-up" data-testid="weekly-recap">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 text-chenia-terracottadark font-semibold"><Sparkles className="h-4 w-4" /> {t("weekly_recap")}{member && <span className="text-muted-foreground font-normal">· {member.name}</span>}</div>
          <p className="mt-1 text-sm text-muted-foreground">{t("recap_sub")}{data && <span className="ml-1 text-xs">({data.week_start.slice(8)}.{data.week_start.slice(5, 7)} – {data.week_end.slice(8)}.{data.week_end.slice(5, 7)})</span>}</p>
        </div>
        {!isRecapDay() && <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground" aria-label={t("hide_recap")} data-testid="recap-hide"><ChevronUp className="h-4 w-4" /></button>}
      </div>
      {data && (
        <>
          <div className="mt-4 rounded-2xl bg-chenia-rosesoft/50 p-4 text-sm leading-relaxed" data-testid="recap-message">
            {data.message || t(data.message_key)}
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Kpi icon={CheckCircle2} value={`${data.consumed}/${data.planned}`} label={t("meals_checked")} testid="recap-meals" />
            <Kpi icon={Droplets} value={`${data.water.avg}/${data.water.goal}`} label={t("water_avg")} testid="recap-water" />
            <Kpi icon={Heart} value={data.favorites_cooked} label={t("favorites_cooked")} testid="recap-favorites" />
            <Kpi icon={ChefHat} value={`${pct(data.avg_kcal_dev)} · ${pct(data.avg_protein_dev)}`} label={`${t("target_gap")} (${t("kcal_short")} · ${t("protein_short")})`} testid="recap-gap" />
          </div>
          {data.top_recipe && (
            <Link to={`/recipe/${data.top_recipe.recipe_id}`} className="mt-3 flex items-center gap-3 rounded-2xl p-2 hover:bg-secondary transition-colors" data-testid="recap-top-recipe">
              <img src={data.top_recipe.image} alt="" className="h-12 w-12 rounded-xl object-cover" style={{ objectPosition: data.top_recipe.focal || "50% 50%" }} />
              <div className="min-w-0"><p className="text-xs uppercase tracking-wide text-muted-foreground">{t("most_cooked_recipe")}</p><p className="font-medium truncate">{tv(data.top_recipe.name)} <span className="text-muted-foreground text-sm">× {data.top_recipe.times}</span></p></div>
            </Link>
          )}
        </>
      )}
    </div>
  );
}
